import { useState, useCallback } from "react";

/**
 * Customer subscription options
 */
export interface CustomerOptions {
    prime: boolean;
    rxPassSubscription: boolean;
    rxPassForceActive: boolean;
    hornbillSubscription: boolean;
    setDefaultAddress: boolean;
    commonAllergies: string[];
    commonHealthConditions: string[];
    pregnantOrNursing: boolean;
    gender: string;
}

/**
 * Hook to manage customer subscription options with dependency logic
 *
 * Dependencies:
 * - RxPass Subscription requires Prime
 * - RxPass Force Active requires Prime + RxPass Subscription
 * - Unchecking Prime unchecks all RxPass options
 * - Unchecking RxPass Subscription unchecks RxPass Force Active
 */
export const useCustomerOptions = (initialOptions?: Partial<CustomerOptions>) => {
    const [options, setOptions] = useState<CustomerOptions>({
        prime: initialOptions?.prime ?? false,
        rxPassSubscription: initialOptions?.rxPassSubscription ?? false,
        rxPassForceActive: initialOptions?.rxPassForceActive ?? false,
        hornbillSubscription: initialOptions?.hornbillSubscription ?? false,
        setDefaultAddress: initialOptions?.setDefaultAddress ?? false,
        commonAllergies: initialOptions?.commonAllergies ?? [],
        commonHealthConditions: initialOptions?.commonHealthConditions ?? [],
        pregnantOrNursing: initialOptions?.pregnantOrNursing ?? false,
        gender: initialOptions?.gender ?? "Male",
    });

    const handlePrimeChange = useCallback((checked: boolean) => {
        if (!checked) {
            // If unchecking Prime, also uncheck RxPass options
            setOptions((prev) => ({
                ...prev,
                prime: false,
                rxPassSubscription: false,
                rxPassForceActive: false,
            }));
        } else {
            setOptions((prev) => ({ ...prev, prime: true }));
        }
    }, []);

    const handleRxPassSubscriptionChange = useCallback((checked: boolean) => {
        if (checked) {
            // If checking RxPass Subscription, ensure Prime is checked
            setOptions((prev) => ({
                ...prev,
                prime: true,
                rxPassSubscription: true,
            }));
        } else {
            // If unchecking RxPass Subscription, also uncheck RxPass Force Active
            setOptions((prev) => ({
                ...prev,
                rxPassSubscription: false,
                rxPassForceActive: false,
            }));
        }
    }, []);

    const handleRxPassForceActiveChange = useCallback((checked: boolean) => {
        if (checked) {
            // If checking RxPass Force Active, ensure Prime and RxPass Subscription are checked
            setOptions((prev) => ({
                ...prev,
                prime: true,
                rxPassSubscription: true,
                rxPassForceActive: true,
            }));
        } else {
            setOptions((prev) => ({ ...prev, rxPassForceActive: false }));
        }
    }, []);

    const handleHornbillSubscriptionChange = useCallback((checked: boolean) => {
        setOptions((prev) => ({ ...prev, hornbillSubscription: checked }));
    }, []);

    const handleSetDefaultAddressChange = useCallback((checked: boolean) => {
        setOptions((prev) => ({ ...prev, setDefaultAddress: checked }));
    }, []);

    const handleCommonAllergiesChange = useCallback((allergyIds: string[]) => {
        setOptions((prev) => ({ ...prev, commonAllergies: allergyIds }));
    }, []);

    const handleCommonHealthConditionsChange = useCallback((conditionIds: string[]) => {
        setOptions((prev) => ({ ...prev, commonHealthConditions: conditionIds }));
    }, []);

    const handlePregnantOrNursingChange = useCallback((checked: boolean) => {
        setOptions((prev) => ({ ...prev, pregnantOrNursing: checked }));
    }, []);

    const handleGenderChange = useCallback((gender: string) => {
        setOptions((prev) => ({ 
            ...prev, 
            gender,
            // Reset pregnantOrNursing if gender is not Female
            pregnantOrNursing: gender === "Female" ? prev.pregnantOrNursing : false
        }));
    }, []);

    const resetOptions = useCallback(() => {
        setOptions({
            prime: false,
            rxPassSubscription: false,
            rxPassForceActive: false,
            hornbillSubscription: false,
            setDefaultAddress: false,
            commonAllergies: [],
            commonHealthConditions: [],
            pregnantOrNursing: false,
            gender: "Male",
        });
    }, []);

    return {
        options,
        handlePrimeChange,
        handleRxPassSubscriptionChange,
        handleRxPassForceActiveChange,
        handleHornbillSubscriptionChange,
        handleSetDefaultAddressChange,
        handleCommonAllergiesChange,
        handleCommonHealthConditionsChange,
        handlePregnantOrNursingChange,
        handleGenderChange,
        resetOptions,
    };
};
